﻿import Map from "ol/Map";
import Geometry from 'ol/geom/Geometry';
import LineString from "ol/geom/LineString";
import LinearRing from "ol/geom/LinearRing";
import Polygon from "ol/geom/Polygon";
import { Pixel } from "ol/pixel";
import { Coordinate } from 'ol/coordinate';
import { ProjectionLike } from "ol/proj";
import { Extent } from "ol/extent";

import GeometryTypeInfo from "../GeometryTypeInfo";
import { Projections } from "../../models/Projections";
import { Coordinates, isCoordinate, isFlatCoordinateArray, isCoordinatesArray } from '../models/Coordinates';
import DateLineUtilities, { DEGREES_90 } from "../../utilities/DateLineUtilities";


export abstract class BaseGeometryExtension<G extends Geometry> {
    constructor(public readonly geometry: G) {
    }

    public abstract getCoordinates(): Coordinates;
    public abstract setCoordinates(coordinates: Coordinates): void;

    public abstract insertGeometry(geometry: Geometry, path: number[]): number[] | undefined;
    public abstract replaceGeometry(geometry: Geometry, path: number[]): number[] | undefined;
    public abstract removeGeometry(path: number[]): number[] | undefined;
    public abstract canRemove(path: number[]): boolean;

    public abstract intersectsExtent(extent: Extent): boolean;
    public abstract groupSectionsAtDateLine(): void;

    public abstract mergeSnappedPoints(): boolean;

    public get supertype() {
        return GeometryTypeInfo.getSuperTypeFor(this.geometry.getType());
    }

    public canEdit(path: number[]): boolean {
        return GeometryExtensionUtilities.walkPath(path, this.getCoordinates(), true) !== undefined;
    }

    public getMultiGeometry(): Geometry | undefined {
        return undefined;
    }

    public getSingleGeometry(): Geometry | undefined {
        return undefined;
    }
    
    public adjustGeometryToBeInserted(geometry: Geometry, path: number[]) {
        return { geometry, path };
    }

    public getRadius(projection: ProjectionLike = Projections.WebMercator): number | undefined {
        return undefined;
    }

    public setRadius(radius: number, projection: ProjectionLike = Projections.WebMercator) {
    }

    public getFlattenCoordinates() {
        return GeometryExtensionUtilities.flattenCoordinates(this.getCoordinates());
    }
}

export class GeometryExtensionUtilities {

    private static helper = new LineString([]);

    public static findClosestPoint(point: Coordinate, data: Coordinates, interpolate: boolean, map: Map, excludePaths?: number[][]) {

        const shouldExclude = (path: number[]) => excludePaths && excludePaths.some(p => this.samePoints(p, path))

        const findBest = (data: Coordinates, path: number[]) => {
            let best = {
                path: [] as number[],
                point: undefined as Coordinate | undefined,
                relativeDistance: undefined as number | undefined,
                pixelDistance: Infinity,
                interpolated: undefined as Coordinate | undefined
            }

            if (shouldExclude(path))
                return best;            

            const coord = isCoordinate(data);
            if (coord) {
                const { sqrDistance } = this.findIndexForPoints(point, [coord], map);
                best.path = [...path];
                best.pixelDistance = sqrDistance;
                best.point = coord;
            }
            else {
                const list = isCoordinatesArray(data);
                if (list) {
                    let arr = isFlatCoordinateArray(list);
                    if (arr) {
                        const coords = arr.map((c, i) => {
                            if (shouldExclude([...path, i])) 
                                return undefined;                
                            else
                                return c;
                        })

                        const { sqrDistance, bestIndex, leftIndex, closest } = interpolate ?
                            this.findIndexForLines(point, coords, map) :
                            this.findIndexForPoints(point, coords, map);

                        if (leftIndex !== undefined && closest) {                            
                            var distance = 0;
                            for (var i = 0; i < leftIndex; i++) {
                                this.helper.setCoordinates([arr[i], arr[i + 1]]);
                                distance += this.helper.getLength();
                            }

                            this.helper.setCoordinates([arr[leftIndex], closest]);
                            distance += this.helper.getLength();

                            this.helper.setCoordinates(arr);
                            best.relativeDistance = distance / this.helper.getLength();
                        }

                        best.path = [...path, bestIndex];
                        best.pixelDistance = sqrDistance;
                        best.point = arr[bestIndex];
                        best.interpolated = closest;
                    }
                    else {
                        list.forEach((data, i) => {
                            const result = findBest(data, [...path, i]);
                            if (result.pixelDistance <= best.pixelDistance)
                                best = { ...result };
                        })
                    }
                }
            }

            return best;
        }

        const result = findBest(data, []);    
        let interpolated: {
            point: Coordinate,
            pixelDistance: number
        } | undefined = undefined;

        if (result.point) {
            result.pixelDistance = Math.sqrt(result.pixelDistance);

            // Recalculate pixeldistance if it was based on interpolated point
            if (interpolate) {
                // Save interpolated point + distance before recalculating pixeldistance
                if (result.interpolated) {
                    interpolated = {
                        point: result.interpolated,
                        pixelDistance: result.pixelDistance
                    }
                }

                result.pixelDistance = Math.sqrt(
                    this.calculateSqrDistance(map.getPixelFromCoordinate(point), map.getPixelFromCoordinate(result.point)));
            }

            return {
                exact: {                    
                    path: result.path,
                    pixelDistance: result.pixelDistance,
                    relativeDistance: result.relativeDistance,
                    point: result.point
                },
                interpolated
            }
        }
        return undefined;
    }

    private static findIndexForLines(coordinate: Coordinate, coordinates: (Coordinate | undefined)[], map: Map) {

        let bestDistance = Infinity;
        let bestClosest: Coordinate | undefined;
        let bestIndex = 0;
        let leftIndex = 0;
        const pixel = map.getPixelFromCoordinate(coordinate);        

        // check every segment on line
        for (var i = 0; i < (coordinates.length - 1); i++) {
            let coord1 = coordinates[i];
            let coord2 = coordinates[i + 1];
            
            let closestCoordinate: Coordinate | undefined ;

            // only if both coordinates are defined we can draw a line segment to find closest coordinate
            if (coord1 !== undefined && coord2 !== undefined) {
                this.helper.setCoordinates([coord1, coord2]);
                closestCoordinate = this.helper.getClosestPoint(coordinate);
            }
            // if one of the two is undefined, tryget the one that is not undefined
            else {
                closestCoordinate = coord1 ?? coord2;
            }

            if (closestCoordinate) {
                const closestPixel = map.getPixelFromCoordinate(closestCoordinate);
                const distance = this.calculateSqrDistance(closestPixel, pixel);

                if (distance <= bestDistance) {
                    bestClosest = closestCoordinate;
                    bestDistance = distance;
                    bestIndex = this.findIndexForPoints(closestCoordinate, [coord1, coord2], map).bestIndex + i;
                    leftIndex = i;
                }
            }           
        }

        return {
            leftIndex,
            bestIndex,
            sqrDistance: bestDistance,
            closest: bestClosest
        }
    }


    private static findIndexForPoints(coordinate: Coordinate, coordinates: (Coordinate | undefined)[], map: Map) {
        let bestDistance = Infinity;
        let bestIndex = 0;
        const pixel = map.getPixelFromCoordinate(coordinate);

        coordinates.forEach((coord, i) => {
            if (coord) {
                const p = map.getPixelFromCoordinate(coord);
                const distance = this.calculateSqrDistance(p, pixel);
                if (distance <= bestDistance) {
                    bestDistance = distance;
                    bestIndex = i;
                }
            }
        })

        return {
            bestIndex,            
            sqrDistance: bestDistance,
            closest: undefined,
            leftIndex: undefined,
        }
    }

    public static walkPath(path: number[], coordinates: Coordinates, extactPath?: boolean): Coordinates | undefined {
        for (var i = 0; i < path.length; i++) {
            const next = coordinates[path[i]];
            if (next !== undefined && typeof next !== 'number')
                coordinates = next;
            else {
                if (extactPath)
                    return undefined;
                break;
            }
        }

        return coordinates;
    }

    public static modifyPointInLine(position: [number], line: Coordinate[], point?: Coordinate, replace?: boolean): Coordinate[] {
        if (point)
            line.splice(position[0], replace ? 1 : 0, point);
        else
            line.splice(position[0], 1);
        return line;
    }

    public static modifyPointInMultiLine(position: [number, number], multiline: Coordinate[][], point?: Coordinate, replace?: boolean) {
        const [x, y] = position
        this.modifyPointInLine([y], multiline[x] ?? [], point, replace);
        return multiline;
    }

    public static modifyPointInMultiPoly(position: [number, number, number], multipoly: Coordinate[][][], point?: Coordinate, replace?: boolean) {
        const [x, y, z] = position
        this.modifyPointInMultiLine([y, z], multipoly[x] ?? [], point, replace);
        return multipoly;
    }

    public static modifyLineInMultiLine(position: [number], multiline: Coordinate[][], line?: Coordinate[], replace?: boolean) {
        if (line)
            multiline.splice(position[0], replace ? 1 : 0, line);
        else
            multiline.splice(position[0], 1);
        return multiline;
    }

    public static modifyLineInMultiPoly(position: [number, number], multiline: Coordinate[][][], line?: Coordinate[], replace?: boolean) {
        const [x, y] = position;
        this.modifyLineInMultiLine([y], multiline[x] ?? [], line, replace);
        return multiline;
    }

    public static modifyMultiLineInMultiPoly(position: [number], multipoly: Coordinate[][][], multiline?: Coordinate[][], replace?: boolean) {
        if (multiline)
            multipoly.splice(position[0], replace ? 1 : 0, multiline);
        else
            multipoly.splice(position[0], 1);
        return multipoly;
    }

    public static getPositionFromPath1(path: number[]): [number] {
        return [path[0] === undefined ? 0 : path[0]];
    }

    public static getPositionFromPath2(path: number[]): [number, number] {
        return [path[0] ?? 0, ...this.getPositionFromPath1(path.slice(1))];
    }

    public static getPositionFromPath3(path: number[]): [number, number, number] {
        return [path[0] ?? 0, ...this.getPositionFromPath2(path.slice(1))];
    }

    public static fixLinearRing(ring: Coordinate[], reference: 'first' | 'last') {
        if (ring) {
            const first = 0;
            const last = ring.length - 1;
            if (!this.samePoints(ring[first], ring[last])) {
                if (reference === 'last')
                    ring[first] = ring[last].slice();
                else
                    ring[last] = ring[first].slice();
            }
        }
    }

    public static isLinearRing(geometry: Geometry) {
        var ring: LinearRing | undefined = undefined;

        if (geometry instanceof LinearRing)
            ring = geometry;
        else if (geometry instanceof Polygon) {
            const rings = geometry.getLinearRings();
            if (rings.length === 1)
                ring = rings[0];
        }

        return ring;
    }

    public static calculateSqrDistance(point1: Pixel | Coordinate, point2: Pixel | Coordinate) {
        var dx = point1[0] - point2[0];
        var dy = point1[1] - point2[1];
        return dx * dx + dy * dy;
    }

    public static samePoints(point1: Pixel | Coordinate | number[], point2: Pixel | Coordinate | number[]) {
        return point1.length === point2.length && point1.every((v, j) => v === point2[j]);
    }

    public static shouldWrapLongitude(x: number) {
        const right = Math.abs(DEGREES_90);
        const left = -right;

        x = DateLineUtilities.setBetweenDateLines(x);
        const leftDistance = Math.abs(x - left);
        const rightDistance = Math.abs(x - right);

        return leftDistance < rightDistance;
    }

    /**
     * Returns merged coordinates for undefined if nno point have been merged
     * @param coordinates
     */
    public static mergeSnappedPoints(coordinates: Coordinate[]) {                      
        let coord0 = coordinates[0];
        const merged: Coordinate[] = [coord0];
        for (var i = 1; i < coordinates.length; i++) {
            let coord1 = coordinates[i];
            if (!this.samePoints(coord0, coord1))
                merged.push(coord1);            
            coord0 = coord1;
        }
        if (merged.length !== coordinates.length)
            return merged;
        return undefined;
    }

    public static containsCoordinate(geometry: Geometry, coordinate: Coordinate) {
        return geometry.intersectsCoordinate(coordinate) || GeometryExtensionUtilities.samePoints(geometry.getClosestPoint(coordinate), coordinate)
    }

    public static flattenCoordinates(coords: Coordinates): Coordinate[] {    

        const point = isCoordinate(coords);
        if (point) 
            return [point];

        const line = isFlatCoordinateArray(coords);
        if (line)
            return [...line];

        const nested = isCoordinatesArray(coords);
        if (nested) {
            const flat: Coordinate[] = [];
            nested.forEach(coords => flat.push(...[...this.flattenCoordinates(coords)]));
            return flat;
        }    

        return [];
    }
}